import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useState } from 'react';

import { CatalogCardProgressBar } from '@/components/CatalogCard/CatalogCardProgressBar';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogIsDone,
    getCatalogIsTimedPlus,
    getCatalogIsTimedPlusClosing,
    getCatalogStartTime,
    getShouldShowLive,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';

import { CatalogBannerActions } from '../BannerActions/CatalogBannerActions';
import { CoverLotsImageCluster } from './CoverLotsImageCluster/CoverLotsImageCluster';
import { LiveImageCluster } from './LiveImageCluster/LiveImageCluster';
import { TimedPlusInProgressCluster } from './TimedPlusInProgressCluster/TimedPlusInProgressCluster';
import { getHeaderImageClusterContainerClassName } from './utils/getHeaderImageClusterContainerClassName';
import { getHeaderImageClusterVariant, ImageClusterVariant } from './utils/getHeaderImageClusterVariant';

type Props = {
    catalogId: number;
    className?: string;
};

export const HeaderImageCluster = ({ catalogId, className }: Props) => {
    const [forceIsLive, setForceIsLive] = useState(false);

    const isLiveFromStore = useAppSelector((state) => getShouldShowLive(state, catalogId));
    const isCatalogDone = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const lotsAreClosing = useAppSelector((state) => getCatalogIsTimedPlusClosing(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const isMobile = useWindowIsMobile();

    const variant = getHeaderImageClusterVariant({
        isLive: forceIsLive || isLiveFromStore,
        isTimedPlus,
        lotsAreClosing,
        saleStartTs,
    });
    const isLiveVariant = variant === ImageClusterVariant.Live;
    const isTimedPlusInProgress = variant === ImageClusterVariant.TimedPlusInProgress;
    const containerClassName = getHeaderImageClusterContainerClassName({
        isCatalogDone,
        variant,
    });

    const handleCountdownComplete = useCallback(() => {
        setForceIsLive(true);
    }, []);

    return (
        <div
            className={cn('relative flex flex-col gap-sm overflow-hidden', containerClassName, className)}
            data-testid="catalog-header-image-cluster"
        >
            {isTimedPlusInProgress ? (
                <TimedPlusInProgressCluster catalogId={catalogId} />
            ) : isLiveVariant ? (
                <LiveImageCluster catalogId={catalogId} />
            ) : (
                <CoverLotsImageCluster
                    catalogId={catalogId}
                    isCatalogDone={isCatalogDone}
                    onCountdownComplete={handleCountdownComplete}
                />
            )}
            {!isCatalogDone && !isMobile && (
                <CatalogBannerActions
                    catalogId={catalogId}
                    forceIsLive={forceIsLive}
                />
            )}
            {isLiveVariant && (
                <CatalogCardProgressBar
                    catalogId={catalogId}
                    className="absolute inset-x-0 bottom-0"
                />
            )}
        </div>
    );
};
